import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useStorage } from '@/js/libs/hooks/useStorage';

import type { StorageScope, StorageKey } from 'src/hooks/useSessionStorage.ts';
import type { FilterObject, PersistedFilterObject, PersistedFilterValue } from './types.ts';

export const useFiltersSessionStorage = <TFilters extends FilterObject>(storageKey: StorageKey, storageScope?: StorageScope) => {
  const { loadJSON, saveJSON } = useStorage<StorageKey, StorageScope>({ scope: storageScope, storage: window.sessionStorage });

  const loadFilters = useCallbackRef(
    (customScope?: string) => loadJSON(storageKey, undefined, customScope) as Partial<PersistedFilterObject<TFilters>> | null,
  );

  const loadStorageFilterField = useCallbackRef((name: keyof TFilters, customScope?: StorageScope): PersistedFilterValue => {
    const values = loadFilters(customScope);
    return values?.[name] ?? null;
  });

  const saveStorageFilterField = useCallbackRef((name: keyof TFilters, value: PersistedFilterValue, customScope?: StorageScope) => {
    const filters: Partial<PersistedFilterObject<TFilters>> = loadFilters(customScope) ?? {};
    filters[name] = value;
    saveJSON(storageKey, filters, customScope);
  });

  const removeStorageFilterField = useCallbackRef((name: keyof TFilters, customScope?: StorageScope) => {
    const filters = loadFilters(customScope);
    if (filters) {
      delete filters[name];
      saveJSON(storageKey, filters, customScope);
    }
  });

  return {
    saveStorageFilterField,
    loadStorageFilterField,
    removeStorageFilterField,
  };
};
